<template>
  <div class="navbar-container-b2b d-flex align-items-center">
    <div class="navbar-zone navbar-zone-left">
      <b-link class="nav-link menu-toggle-btn d-xl-none" @click="toggleVerticalMenuActive">
        <feather-icon icon="MenuIcon" size="18" />
      </b-link>
      <b-link
        class="nav-link menu-collapse-btn d-none d-xl-flex"
        @click="toggleMenuCollapsed"
        v-b-tooltip.hover.bottom="isMenuCollapsed ? 'Expandir Menu' : 'Recolher Menu'"
      >
        <feather-icon :icon="isMenuCollapsed ? 'ChevronsRightIcon' : 'ChevronsLeftIcon'" size="16" />
      </b-link>
    </div>

    <div class="navbar-zone navbar-zone-center">
      <GlobalSmartSearch v-if="isLogged" />
    </div>

    <div class="navbar-zone navbar-zone-right" v-if="isLogged">
      <button
        class="icon-btn"
        @click="toggleTheme"
        v-b-tooltip.hover.bottom="isDarkMode ? 'Modo Claro' : 'Modo Escuro'"
      >
        <feather-icon :icon="isDarkMode ? 'SunIcon' : 'MoonIcon'" size="16" />
      </button>
      <button
        class="icon-btn"
        v-b-modal.modal-center-info-pedido
        v-b-tooltip.hover.bottom="'Modal Info. Pedido'"
      >
        <feather-icon icon="SettingsIcon" size="16" />
      </button>

      <!-- Consulta pessoal por representante, conforme o perfil ativo. -->
      <div v-if="exibeBudgetPessoal" class="icon-btn-wrapper budget-widget-wrapper">
        <budget-saldo-widget />
      </div>

      <b-nav-item-dropdown
        right
        toggle-class="user-dropdown-toggle"
        class="user-dropdown"
        no-caret
      >
        <template #button-content>
          <div class="user-avatar">
            <feather-icon icon="UserIcon" size="14" />
          </div>
          <div class="user-info d-none d-md-flex">
            <span class="user-name">{{ userData.nome ? userData.nome : userData.userData.nome }}</span>
            <span class="user-email">{{ userData.email ? userData.email : userData.userData.email }}</span>
          </div>
          <feather-icon icon="ChevronDownIcon" size="12" class="dropdown-arrow d-none d-sm-block" />
        </template>
        <b-dropdown-item link-class="d-flex align-items-center profile-item" @click="openProfileModal">
          <feather-icon size="14" icon="UserIcon" class="mr-50" />
          <span>Meu Perfil</span>
        </b-dropdown-item>
        <b-dropdown-item link-class="d-flex align-items-center profile-item" @click="openChangePasswordModal">
          <feather-icon size="14" icon="LockIcon" class="mr-50" />
          <span>Alterar senha</span>
        </b-dropdown-item>
        <template v-if="tenantHelp">
          <b-dropdown-divider />
          <b-dropdown-item
            :to="{ name: 'documentation', params: { tenantSlug: $route.params.tenantSlug } }"
            link-class="d-flex align-items-center profile-item"
            data-testid="user-documentation-link"
          >
            <feather-icon size="14" icon="BookOpenIcon" class="mr-50" />
            <span>Documentação</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="tenantHelp.videosUrl"
            :href="tenantHelp.videosUrl"
            target="_blank"
            rel="noopener noreferrer"
            link-class="d-flex align-items-center profile-item"
            data-testid="user-videos-link"
          >
            <feather-icon size="14" icon="VideoIcon" class="mr-50" />
            <span>Vídeos</span>
            <feather-icon size="12" icon="ExternalLinkIcon" class="ml-auto" aria-hidden="true" />
          </b-dropdown-item>
        </template>
        <b-dropdown-divider />
        <b-dropdown-item link-class="d-flex align-items-center logout-item" @click="logout()">
          <feather-icon size="14" icon="LogOutIcon" class="mr-50" />
          <span>Sair</span>
        </b-dropdown-item>
      </b-nav-item-dropdown>

      <div class="icon-btn-wrapper cart-wrapper" id="cartBtn">
        <cart-dropdown />
      </div>
    </div>

    <representante-profile-modal ref="profileModal" />
    <b-modal
      ref="changePasswordModal"
      title="Alterar senha"
      centered
      :ok-title="changingPassword ? 'Alterando...' : 'Alterar senha'"
      cancel-title="Cancelar"
      :ok-disabled="changingPassword"
      :cancel-disabled="changingPassword"
      @ok="changePassword"
      @hidden="resetChangePasswordForm"
    >
      <p class="text-muted mb-2">Por segurança, informe sua senha atual para definir uma nova senha.</p>
      <b-alert v-if="changePasswordError" variant="danger" show class="mb-2">
        {{ changePasswordError }}
      </b-alert>
      <b-form-group label="Senha atual" label-for="current-password">
        <b-form-input
          id="current-password"
          v-model="changePasswordForm.senhaAtual"
          type="password"
          autocomplete="current-password"
          :disabled="changingPassword"
        />
      </b-form-group>
      <b-form-group label="Nova senha" label-for="new-password">
        <b-form-input
          id="new-password"
          v-model="changePasswordForm.novaSenha"
          type="password"
          autocomplete="new-password"
          :disabled="changingPassword"
        />
        <small class="text-muted">Use pelo menos 8 caracteres.</small>
      </b-form-group>
      <b-form-group label="Confirme a nova senha" label-for="confirm-new-password" class="mb-0">
        <b-form-input
          id="confirm-new-password"
          v-model="changePasswordForm.confirmacaoNovaSenha"
          type="password"
          autocomplete="new-password"
          :disabled="changingPassword"
        />
      </b-form-group>
    </b-modal>

    <div v-if="!isLogged" class="navbar-zone navbar-zone-right">
      <button
        class="icon-btn"
        @click="toggleTheme"
        v-b-tooltip.hover.bottom="isDarkMode ? 'Modo Claro' : 'Modo Escuro'"
      >
        <feather-icon :icon="isDarkMode ? 'SunIcon' : 'MoonIcon'" size="16" />
      </button>

      <b-link :to="{ name: 'login'}" class="login-btn">
        <feather-icon icon="LogInIcon" size="14" class="mr-50" />
        Entrar
      </b-link>
    </div>
  </div>
</template>

<script>
import {
  BAlert, BFormGroup, BFormInput, BLink, BModal, BNavbarNav, BNavItemDropdown, BDropdownItem, BDropdownDivider, BAvatar, VBTooltip, VBModal,
} from 'bootstrap-vue';
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue';
import useJwt from '@/auth/jwt/useJwt';
import { initialAbility } from '@/libs/acl/config';
import LogoApp from '@/components/LogoApp.vue';
import CartDropdown from '@core/layouts/components/app-navbar/components/CartDropdown.vue';
import BudgetSaldoWidget from '@core/layouts/components/app-navbar/components/BudgetSaldoWidget.vue';
import GlobalSmartSearch from '@/components/GlobalSmartSearch.vue';
import RepresentanteProfileModal from '@/components/modal/RepresentanteProfileModal.vue';
import AuthService from '@/services/Auth/auth.service';
import { getTenantHelpConfig } from '@/documentation/tenantHelp';

export default {
  components: {
    BudgetSaldoWidget,
    CartDropdown,
    LogoApp,
    GlobalSmartSearch,
    RepresentanteProfileModal,
    BAlert,
    BFormGroup,
    BFormInput,
    BLink,
    BModal,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,
    DarkToggler,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-modal': VBModal,
  },
  data() {
    return {
      VUE_APP_HOST_API_BACKEND: process.env.VUE_APP_HOST_API_BACKEND,
      changePasswordForm: {
        senhaAtual: '',
        novaSenha: '',
        confirmacaoNovaSenha: '',
      },
      changePasswordError: '',
      changingPassword: false,
    };
  },
  computed: {
    tenantHelp() {
      return getTenantHelpConfig(this.$route.params.tenantSlug);
    },
    userData() {
      return JSON.parse(localStorage.getItem('userData'));
    },
    isLogged() {
      return localStorage.getItem('userData') && localStorage.getItem(useJwt.jwtConfig.storageTokenKeyName);
    },
    isMenuCollapsed() {
      return this.$store.state.verticalMenu.isVerticalMenuCollapsed;
    },
    isDarkMode() {
      return this.$store.state.appConfig.layout.skin === 'dark';
    },
    // Perfil ativo vem do state reativo de preferências (espelho do
    // localStorage 'perfilLogado' — não ler localStorage direto em computed).
    exibeBudgetPessoal() {
      return ['representante', 'supervisor', 'comercial', 'administrador', 'admindkp'].includes(String(this.$store.state.preferencias.perfilNome || '').trim().toLowerCase());
    },
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    openProfileModal() {
      this.$refs.profileModal.openModal();
    },
    openChangePasswordModal() {
      this.changePasswordError = '';
      this.$refs.changePasswordModal.show();
    },
    resetChangePasswordForm() {
      this.changePasswordForm = {
        senhaAtual: '',
        novaSenha: '',
        confirmacaoNovaSenha: '',
      };
      this.changePasswordError = '';
      this.changingPassword = false;
    },
    async changePassword(event) {
      event.preventDefault();
      this.changePasswordError = '';

      if (!this.changePasswordForm.senhaAtual || !this.changePasswordForm.novaSenha || !this.changePasswordForm.confirmacaoNovaSenha) {
        this.changePasswordError = 'Preencha todos os campos.';
        return;
      }
      if (this.changePasswordForm.novaSenha.length < 8) {
        this.changePasswordError = 'A nova senha deve ter pelo menos 8 caracteres.';
        return;
      }
      if (this.changePasswordForm.novaSenha !== this.changePasswordForm.confirmacaoNovaSenha) {
        this.changePasswordError = 'A confirmação da nova senha não confere.';
        return;
      }

      this.changingPassword = true;
      try {
        await new AuthService().alterarSenha(this.changePasswordForm);
        this.$refs.changePasswordModal.hide();
        this.logout();
      } catch (error) {
        this.changePasswordError = error.message || 'Não foi possível alterar a senha. Tente novamente.';
      } finally {
        this.changingPassword = false;
      }
    },
    toggleMenuCollapsed() {
      this.$store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', !this.isMenuCollapsed);
    },
    toggleTheme() {
      const newSkin = this.isDarkMode ? 'light' : 'dark';
      this.$store.commit('appConfig/UPDATE_SKIN', newSkin);
      localStorage.setItem('vuexy-skin', newSkin);
      document.documentElement.setAttribute('data-theme', newSkin);
    },
    logout() {
      this.$store.dispatch('auth/LOGOUT_USER');
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);
      localStorage.removeItem('lgn_admin');
      localStorage.removeItem('lgn_admin_metadata');
      this.$ability.update(initialAbility);
      // LOGOUT_USER faz navegação completa para descartar o estado em memória.
    },
  },
};
</script>

<style lang="scss">
.navbar-container-b2b {
  width: 100%;
  padding: 0 1rem;
  gap: 0.75rem;
  min-height: 48px;

  .navbar-zone {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .navbar-zone-left {
    flex-shrink: 0;
  }

  .navbar-zone-center {
    flex: 1;
    display: flex;
    justify-content: center;
    max-width: 600px;
    margin: 0 auto;
  }

  .navbar-zone-right {
    flex-shrink: 0;
    gap: 0.375rem;
  }

  .menu-toggle-btn,
  .menu-collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: var(--header-control-bg);
    border: 1px solid var(--header-control-border);
    border-radius: 6px;
    transition: all 0.2s ease;

    svg {
      color: var(--header-control-icon);
    }

    &:hover {
      background: var(--header-control-hover-bg);
      border-color: var(--header-control-hover-border);

      svg {
        color: var(--header-control-icon-hover);
      }
    }
  }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: var(--header-control-bg);
    border: 1px solid var(--header-control-border);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
      color: var(--header-control-icon);
    }

    &:hover {
      background: var(--header-control-hover-bg);
      border-color: var(--header-control-hover-border);

      svg {
        color: var(--header-control-icon-hover);
      }
    }
  }

  .icon-btn-wrapper {
    .nav-link,
    .dropdown-cart {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      background: var(--header-control-bg);
      border: 1px solid var(--header-control-border);
      border-radius: 6px;
      transition: all 0.2s ease;

      svg {
        color: var(--header-control-icon);
      }

      &:hover {
        background: var(--header-control-hover-bg);
        border-color: var(--header-control-hover-border);

        svg {
          color: var(--header-control-icon-hover);
        }
      }
    }

    // O chip de budget (ELT/GL) é mais largo que os botões de ícone: não
    // pode herdar os 32px fixos acima, senão o texto vaza por cima dos
    // controles vizinhos.
    &.budget-widget-wrapper {
      list-style: none;

      &::marker {
        content: '';
      }

      .nav-link {
        width: auto;
        min-width: 32px;
        padding: 0 0.15rem;
        border-radius: 16px;
      }
    }

    &.cart-wrapper .badge {
      position: absolute;
      top: -6px;
      right: -6px;
      background: #dc2626;
      color: #fff;
      font-size: 0.625rem;
      font-weight: 600;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid var(--header-bg);
    }
  }

  .user-dropdown {
    // O b-nav-item-dropdown é um <li>; fora de <ul> o navegador desenha o
    // marcador de lista (bolinha) ao lado da engrenagem, roubando o clique.
    list-style: none;

    &::marker {
      content: '';
    }

    .user-dropdown-toggle {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.25rem 0.5rem 0.25rem 0.25rem;
      background: var(--header-user-bg);
      border: 1px solid var(--header-user-border);
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: var(--header-user-hover-bg);
        border-color: var(--header-control-hover-border);
      }

      &::after {
        display: none;
      }
    }

    .user-avatar {
      width: 28px;
      height: 28px;
      background: var(--sf-btn-primary-bg, var(--primary, #6366f1));
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      svg {
        color: #fff;
      }
    }

    .user-info {
      flex-direction: column;
      text-align: left;
      line-height: 1.2;
      max-width: 140px;
    }

    .user-name {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--header-user-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-email {
      font-size: 0.6875rem;
      color: var(--header-user-text-muted);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dropdown-arrow {
      color: var(--header-user-text-muted);
      margin-left: 0.25rem;
      transition: transform 0.2s ease;
    }

    &.show .dropdown-arrow {
      transform: rotate(180deg);
    }

    .dropdown-menu {
      background: var(--dropdown-bg);
      border: 1px solid var(--dropdown-border);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
      padding: 0.375rem;
      min-width: 160px;
      margin-top: 0.25rem;

      .dropdown-item {
        font-size: 0.8125rem;
        color: var(--dropdown-text);
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        transition: all 0.15s ease;

        svg {
          color: var(--text-muted);
        }

        &:hover {
          background: var(--dropdown-hover);
        }
      }

      .profile-item:hover,
      .settings-item:hover {
        background: var(--dropdown-hover);
      }

      .logout-item:hover {
        background: rgba(220, 38, 38, 0.1);
        color: #dc2626;

        svg {
          color: #dc2626;
        }
      }

      .dropdown-divider {
        margin: 0.25rem 0.5rem;
        border-color: var(--border-color, #e5e7eb);
      }
    }
  }

  .login-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    background: var(--sf-btn-primary-bg, var(--primary, #6366f1));
    color: #fff;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.8125rem;
    transition: all 0.2s ease;

    svg {
      color: #fff;
    }

    &:hover {
      opacity: 0.9;
      color: #fff;
    }
  }
}

.header-navbar {
  background: var(--header-bg);
  border-bottom: 1px solid var(--header-border);
  box-shadow: var(--header-shadow);
  min-height: 48px;

  .navbar-container {
    padding: 0;
  }
}

body:not(.dark-layout) {
  --header-bg: #f8fafc;
  --header-border: #e2e8f0;
  --header-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  --header-control-bg: #ffffff;
  --header-control-border: #e2e8f0;
  --header-control-hover-bg: #f1f5f9;
  --header-control-hover-border: #cbd5e1;
  --header-control-icon: #64748b;
  --header-control-icon-hover: #334155;
  --header-user-bg: #ffffff;
  --header-user-border: #e2e8f0;
  --header-user-hover-bg: #f1f5f9;
  --header-user-text: #1e293b;
  --header-user-text-muted: #64748b;
  --header-input-bg: #ffffff;
  --header-text-primary: #1e293b;
  --dropdown-bg: #ffffff;
  --dropdown-border: #e2e8f0;
  --dropdown-text: #334155;
  --dropdown-hover: #f1f5f9;
}

body.dark-layout {
  --header-bg: #1e293b;
  --header-border: #334155;
  --header-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  --header-control-bg: #334155;
  --header-control-border: #475569;
  --header-control-hover-bg: #475569;
  --header-control-hover-border: #64748b;
  --header-control-icon: #94a3b8;
  --header-control-icon-hover: #e2e8f0;
  --header-user-bg: #334155;
  --header-user-border: #475569;
  --header-user-hover-bg: #475569;
  --header-user-text: #f1f5f9;
  --header-user-text-muted: #94a3b8;
  --header-input-bg: #334155;
  --header-text-primary: #f1f5f9;
  --dropdown-bg: #1e293b;
  --dropdown-border: #334155;
  --dropdown-text: #e2e8f0;
  --dropdown-hover: #334155;
}
</style>
